import axios from 'axios';
import { API_CONFIG } from '../config/apiConfig';

class BackendApiService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.prefix = API_CONFIG.API_PREFIX;
  }

  buildUrl(path) {
    return `${this.baseUrl}${this.prefix}${path}`;
  }

  buildQuery(params = {}) {
    const entries = Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '');
    if (!entries.length) return '';
    const qs = entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(Array.isArray(v) ? v.join(',') : v)}`)
      .join('&');
    return `?${qs}`;
  }

  _buildHeaders() {
    const token = localStorage.getItem('auth_token');
    const headers = { ...API_CONFIG.DEFAULT_HEADERS };
    if (token && token !== 'null' && token !== 'undefined') {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async get(path) {
    const url = this.buildUrl(path);
    const response = await axios.get(url, { headers: this._buildHeaders(), timeout: API_CONFIG.TIMEOUT.DEFAULT });
    return response.data;
  }

  async post(path, data = {}) {
    const url = this.buildUrl(path);
    const response = await axios.post(url, data, { headers: this._buildHeaders(), timeout: API_CONFIG.TIMEOUT.DEFAULT });
    return response.data;
  }

  // Health & Stats
  async getHealth() {
    const headers = this._buildHeaders();
    const timeout = API_CONFIG.TIMEOUT.HEALTH_CHECK || API_CONFIG.TIMEOUT.DEFAULT;
    const candidates = [
      `${this.baseUrl}${this.prefix}/health`,
      `${this.baseUrl}/health`,
      `${this.baseUrl}${this.prefix}/status`,
      `${this.baseUrl}/status`,
    ];
    for (const url of candidates) {
      try {
        const response = await axios.get(url, { headers, timeout });
        return response.data ?? { ok: true };
      } catch (_e) {
        // tenta próximo
      }
    }
    // Se todas falharem, propaga erro
    throw new Error('Health check falhou em todas as rotas conhecidas');
  }
  async getStats() {
    const data = await this.get('/stats');
    // Normalizar para o formato esperado pelo Dashboard
    return {
      totalOrdens: data.totalOrders ?? data.total ?? 0,
      ordensConcluidas: data.completedOrders ?? data.concluded ?? 0,
      totalValorServico: data.totalServiceValue ?? data.serviceValue ?? 0,
      totalValorMaterial: data.totalMaterialValue ?? data.materialValue ?? 0,
      // Permite passar extras sem quebrar chamadas existentes
      raw: data
    };
  }

  // Customers
  async getCustomers() { return this.get('/customers'); }
  async getCustomer(id) { return this.get(`/customers/${id}`); }
  async getCustomerOrders(id) { return this.get(`/customers/${id}/orders`); }
  async getCustomersOrdersComplete(codes = [], options = {}) {
    const pathCodes = Array.isArray(codes) ? codes.join(',') : String(codes);
    const qs = this.buildQuery(options);
    return this.get(`/customers/${pathCodes}/orders/complete${qs}`);
  }

  // Orders
  async getOrders() { return this.get('/orders'); }
  async getOrder(id) { return this.get(`/orders/${id}`); }
  async getOrderItems(id) { return this.get(`/orders/${id}/items`); }
  async getOrderMaterials(id) { return this.get(`/orders/${id}/materials`); }
  async getOrderServices(id) { return this.get(`/orders/${id}/services`); }
  async getOrderTimes(id) { return this.get(`/orders/${id}/times`); }
  async getOrderComplete(id) { return this.get(`/orders/${id}/complete`); }
  async getOrdersSummary() { return this.get('/orders/summary'); }

  // Integrator controls
  async getIntegratorStatus() { return this.get('/integrator/status'); }
  async integratorStart() { return this.post('/integrator/start'); }
  async integratorStop() { return this.post('/integrator/stop'); }
  async integratorRestart() { return this.post('/integrator/restart'); }
}

const backendApiService = new BackendApiService();
export default backendApiService;
